// src/pages/SuperAdmin.jsx
import React, { useState, useEffect } from 'react';
import {
  FiShield, FiUsers, FiSettings, FiDatabase, FiRefreshCw, FiUserPlus,
  FiBarChart2, FiTrendingUp, FiActivity, FiGlobe, FiLock, FiCheck,
  FiAlertTriangle, FiDollarSign, FiCalendar, FiClock, FiEye
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { tenantDataService } from '../services/tenantDataService';
import { db, functions } from '../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import toast from 'react-hot-toast';
import TenantManagement from '../components/admin/TenantManagement';
import TenantCreator from '../components/admin/TenantCreator';
import DataIsolationMonitor from '../components/admin/DataIsolationMonitor';
import CreateClinicModal from '../components/admin/CreateClinicModal';
import TenantIsolationTest from '../components/admin/TenantIsolationTest';
import UserLoginDiagnostic from '../components/admin/UserLoginDiagnostic';

const SUPER_ADMIN_TABS = [
  { key: "analytics", label: "Analytics Dashboard", icon: FiBarChart2 },
  { key: "tenants", label: "Tenant Management", icon: FiGlobe },
  { key: "isolation", label: "Data Isolation", icon: FiLock },
  { key: "users", label: "User Management", icon: FiUsers },
  { key: "system", label: "System Health", icon: FiActivity },
];

const SuperAdmin = () => {
  const { userProfile } = useAuth();
  const { currentTenant, availableTenants, switchTenant } = useTenant();
  const [activeTab, setActiveTab] = useState('analytics');
  const [platformStats, setPlatformStats] = useState(null);
  const [tenantStats, setTenantStats] = useState([]);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTenantCreator, setShowTenantCreator] = useState(false);
  const [showCreateClinic, setShowCreateClinic] = useState(false);

  // Load platform analytics and stats
  useEffect(() => {
    loadPlatformAnalytics();
  }, []);

  const loadPlatformAnalytics = async () => {
    try {
      setLoading(true);

      // Load all tenants
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      const tenants = tenantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Calculate platform-wide statistics
      const stats = {
        totalTenants: tenants.length,
        activeTenants: tenants.filter(t => t.status === 'active').length,
        totalUsers: 0,
        totalClients: 0,
        totalAppointments: 0,
        totalRevenue: 0,
        storageUsed: 0
      };

      // Load detailed stats for each tenant
      const tenantStatsPromises = tenants.map(async (tenant) => {
        try {
          // Set tenant context to get scoped data
          tenantDataService.setTenantContext(tenant.id);

          const [clients, appointments, users] = await Promise.all([
            tenantDataService.getAll('clients').catch(() => []),
            tenantDataService.getAll('appointments').catch(() => []),
            getDocs(query(collection(db, 'users'), where('tenantId', '==', tenant.id))).catch(() => ({ docs: [] }))
          ]);

          const tenantStat = {
            ...tenant,
            clientCount: clients.length,
            appointmentCount: appointments.length,
            userCount: users.docs?.length || 0,
            lastActivity: tenant.lastActivity || new Date().toISOString(),
            storageUsed: Math.random() * 100, // TODO: Calculate actual storage
            revenue: appointments.reduce((sum, apt) => sum + (apt.fee || 0), 0)
          };

          // Add to platform totals
          stats.totalUsers += tenantStat.userCount;
          stats.totalClients += tenantStat.clientCount;
          stats.totalAppointments += tenantStat.appointmentCount;
          stats.totalRevenue += tenantStat.revenue;
          stats.storageUsed += tenantStat.storageUsed;

          return tenantStat;
        } catch (error) {
          console.error(`Error loading stats for tenant ${tenant.id}:`, error);
          return { ...tenant, clientCount: 0, appointmentCount: 0, userCount: 0, revenue: 0 };
        }
      });

      const tenantStatsResults = await Promise.all(tenantStatsPromises);

      setPlatformStats(stats);
      setTenantStats(tenantStatsResults);

      // System health check
      setSystemHealth({
        firebase: 'healthy',
        firestore: 'healthy',
        storage: 'healthy',
        auth: 'healthy',
        lastCheck: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error loading platform analytics:', error);
      toast.error('Failed to load platform analytics');
    } finally {
      setLoading(false);
    }
  };

  // Check super admin permissions
  if (userProfile?.role?.toLowerCase() !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-xl p-8 text-center border border-gray-200">
            <FiShield className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Super Admin Access Required
            </h2>
            <p className="text-gray-600 mb-4">
              This area is restricted to super administrators only.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Required Role:</strong> super_admin<br />
                <strong>Your Role:</strong> {userProfile?.role || 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading platform analytics...</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'analytics':
        return (
          <div className="space-y-6">
            {/* Platform Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiGlobe className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                    <p className="text-2xl font-bold text-gray-900">{platformStats?.totalTenants || 0}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-green-600">
                    {platformStats?.activeTenants || 0} active
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FiUsers className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-bold text-gray-900">{platformStats?.totalUsers || 0}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-500">
                    Across all clinics
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FiCalendar className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Appointments</p>
                    <p className="text-2xl font-bold text-gray-900">{platformStats?.totalAppointments || 0}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-500">
                    Platform-wide
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FiDollarSign className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold text-gray-900">£{(platformStats?.totalRevenue || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-500">
                    All-time
                  </span>
                </div>
              </div>
            </div>

            {/* Tenant Performance Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Tenant Performance</h3>
                <p className="text-sm text-gray-600">Overview of all clinic tenants and their key metrics</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinic</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Users</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clients</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Activity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenantStats.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div
                                className="h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                style={{ backgroundColor: tenant.branding?.primaryColor || '#2563eb' }}
                              >
                                {tenant.name?.charAt(0) || 'T'}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{tenant.name}</div>
                              <div className="text-sm text-gray-500">{tenant.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                            {tenant.status || 'active'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.userCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.clientCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tenant.appointmentCount}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">£{tenant.revenue?.toLocaleString() || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(tenant.lastActivity).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => switchTenant(tenant)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            <FiEye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'tenants':
        return (
          <div className="space-y-6">
            {/* Tenant Management Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Tenant Management</h2>
                <p className="text-gray-600">Create and manage clinic tenants with secure data isolation</p>
              </div>
              <button
                onClick={() => setShowCreateClinic(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center space-x-2"
              >
                <FiUserPlus className="h-5 w-5" />
                <span>Create New Clinic</span>
              </button>
            </div>

            {/* Current Tenant Info */}
            {currentTenant && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Currently Managing</h3>
                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-medium"
                    style={{ backgroundColor: currentTenant?.branding?.primaryColor || '#2563eb' }}
                  >
                    {currentTenant?.name?.charAt(0) || 'T'}
                  </div>
                  <div>
                    <p className="text-lg font-medium text-gray-900">{currentTenant.name}</p>
                    <p className="text-sm text-gray-500">{currentTenant.slug}</p>
                    <p className="text-sm text-gray-600">{currentTenant.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tenant Management Component */}
            <TenantManagement onTenantSelect={switchTenant} />
          </div>
        );

      case 'isolation':
        return (
          <div className="space-y-8">
            <DataIsolationMonitor />
            <TenantIsolationTest />
          </div>
        );

      case 'users':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">User Management</h3>
              <p className="text-gray-600">Manage users across all tenant organizations</p>
              {/* TODO: Implement user management interface */}
            </div>
            <UserLoginDiagnostic />
          </div>
        );

      case 'system':
        return (
          <div className="space-y-6">
            {/* System Health Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FiCheck className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Firebase</p>
                    <p className="text-lg font-bold text-green-600">{systemHealth?.firebase || 'Healthy'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FiDatabase className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Firestore</p>
                    <p className="text-lg font-bold text-blue-600">{systemHealth?.firestore || 'Healthy'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FiLock className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Authentication</p>
                    <p className="text-lg font-bold text-purple-600">{systemHealth?.auth || 'Healthy'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FiActivity className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Storage</p>
                    <p className="text-lg font-bold text-yellow-600">{systemHealth?.storage || 'Healthy'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Platform Stats</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Total Storage Used:</span>
                      <span>{(platformStats?.storageUsed || 0).toFixed(1)} MB</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Active Tenants:</span>
                      <span>{platformStats?.activeTenants || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Users:</span>
                      <span>{platformStats?.totalUsers || 0}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Last Health Check</h4>
                  <div className="text-sm text-gray-600">
                    {systemHealth?.lastCheck ? new Date(systemHealth.lastCheck).toLocaleString() : 'Never'}
                  </div>
                  <button
                    onClick={loadPlatformAnalytics}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    Run Health Check
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <p className="text-gray-500">Select a tab to view content</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <FiShield className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Super Admin</h1>
                <p className="text-sm text-gray-600">System administration and multi-tenant management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Welcome, {userProfile?.firstName || 'Admin'}
              </div>
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {userProfile?.firstName?.charAt(0) || 'A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {SUPER_ADMIN_TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>

      {/* Tenant Creator Modal */}
      <TenantCreator
        isOpen={showTenantCreator}
        onClose={() => setShowTenantCreator(false)}
        onTenantCreated={(tenant) => {
          // Refresh analytics after creating tenant
          loadPlatformAnalytics();
          toast.success(`Tenant "${tenant.name}" created successfully!`);
        }}
      />

      {/* Create Clinic Modal */}
      <CreateClinicModal
        isOpen={showCreateClinic}
        onClose={() => setShowCreateClinic(false)}
        onClinicCreated={(result) => {
          // Refresh analytics after creating clinic
          loadPlatformAnalytics();
          toast.success(`Clinic "${result.clinicName}" provisioned successfully!`);
          setShowCreateClinic(false);
        }}
      />
    </div>
  );
};

export default SuperAdmin;
